import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Toaster } from '@/components/ui/sonner';
import App from './App.tsx';
import { AuthProvider } from './context/AuthProvider.tsx';
import { BrandProvider } from './context/BrandProvider.tsx';
import { initSentry } from './lib/sentry.ts';
import { validateEnv } from './lib/env';
import './index.css';

// Polyfill crypto.randomUUID() for insecure contexts (HTTP, old WebView)
// where the Web Crypto API requires a secure origin.
if (!crypto.randomUUID) {
  crypto.randomUUID = () =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    }) as `${string}-${string}-${string}-${string}-${string}`;
}

let initFailed = false;

try {
  validateEnv();
} catch (e) {
  initFailed = true;
  const root = document.getElementById('root');
  if (root) {
    const msg = e instanceof Error ? e.message : 'Missing required environment variables.';
    const container = Object.assign(document.createElement('div'), {
      style:
        'display:flex;min-height:100vh;align-items:center;justify-content:center;font-family:system-ui,sans-serif',
    });
    const card = Object.assign(document.createElement('div'), {
      style: 'max-width:28rem;padding:2rem;text-align:center',
    });
    const h1 = Object.assign(document.createElement('h1'), {
      style: 'font-size:1.5rem;font-weight:700;margin-bottom:1rem',
      textContent: 'Configuration Error',
    });
    const p = Object.assign(document.createElement('p'), {
      style: 'color:#666;margin-bottom:1rem;white-space:pre-wrap',
      textContent: msg,
    });
    const note = Object.assign(document.createElement('p'), {
      style: 'font-size:0.875rem;color:#999',
      textContent: 'Check that .env contains all required variables from .env.example',
    });
    card.append(h1, p, note);
    container.append(card);
    root.replaceChildren(container);
  }
}

// Only mount React if env vars are present — otherwise the DOM already
// shows the configuration error above and there's nothing more to do.
if (!initFailed) {
  initSentry();

  const queryClient = new QueryClient();

  createRoot(document.getElementById('root')!).render(
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            {/* BrandProvider sits below QueryClient (needs React Query) and
                below Auth (needs the user id) but above the route tree so
                every page receives the effective brand + CSS variables. */}
            <BrandProvider>
              <App />
              <Toaster />
              <Analytics />
              <SpeedInsights />
            </BrandProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>,
  );
}
